import React, { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ReactNode;
  current?: boolean;
  badge?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [tripsOpen, setTripsOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);

  const isAnalyticsPath = useMemo(() => location.pathname.startsWith('/analytics'), [location.pathname]);
  const isExpensesPath = useMemo(() => location.pathname.startsWith('/expenses'), [location.pathname]);
  const isTripsPath = useMemo(() => location.pathname.startsWith('/trips'), [location.pathname]);
  const isCommunityPath = useMemo(() => location.pathname.startsWith('/community'), [location.pathname]);

  // Keep Analytics expanded when navigating within /analytics
  React.useEffect(() => {
    if (isAnalyticsPath) setAnalyticsOpen(true);
  }, [isAnalyticsPath]);

  // Keep Expenses expanded when navigating within /expenses
  React.useEffect(() => {
    if (isExpensesPath) setExpensesOpen(true);
  }, [isExpensesPath]);

  // Keep Trips expanded when navigating within /trips
  React.useEffect(() => {
    if (isTripsPath) setTripsOpen(true);
  }, [isTripsPath]);

  // Keep Community expanded when navigating within /community
  React.useEffect(() => {
    if (isCommunityPath) setCommunityOpen(true);
  }, [isCommunityPath]);

  const navigation: NavigationItem[] = [
    {
      name: 'Dashboard',
      href: '/',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v3H8V5z" />
        </svg>
      ),
    },
    {
      name: 'Credit Cards',
      href: '/cards',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      name: 'Rewards',
      href: '/rewards',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      name: 'Milestones',
      href: '/milestones',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      name: 'Banks',
      href: '/banks',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      name: 'Categories',
      href: '/categories',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
    },
    // Analytics is rendered as an expandable section below
    {
      name: 'Users',
      href: '/users',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
    },
    {
      name: 'User Cards',
      href: '/user-cards',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h8m-8 4h8m-8 4h4" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
    },
    {
      name: 'Blogs',
      href: '/blogs',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 20h9M12 4h9M4 9h16M4 15h16"
          />
        </svg>
      ),
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ];

  const isCurrentPath = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col lg:z-50">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 px-4">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z"/>
                </svg>
              </div>
              <h1 className="ml-3 text-xl font-bold text-gray-900">Rewards Admin</h1>
            </div>
          </div>

          {/* Navigation */}
          <nav className="mt-8 flex-1 px-2 space-y-1">
            {navigation.map((item) => {
              const current = isCurrentPath(item.href);
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                    current
                      ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={onClose}
                >
                  <div className={`mr-3 flex-shrink-0 ${current ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}`}>
                    {item.icon}
                  </div>
                  {item.name}
                  {item.badge && (
                    <span className="ml-auto inline-block py-0.5 px-2 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}

            {/* Expenses (expandable) */}
            <div>
              <button
                type="button"
                className={`group w-full flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isExpensesPath
                    ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                onClick={() => setExpensesOpen((v) => !v)}
              >
                <div
                  className={`mr-3 flex-shrink-0 ${
                    isExpensesPath ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m0 0l2 2m-2-2l-2-2" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 11h6m-6 4h6" />
                  </svg>
                </div>
                <span className="flex-1 text-left">Expenses</span>
                <svg
                  className={`w-5 h-5 transition-transform ${expensesOpen ? 'transform rotate-90' : ''} ${
                    isExpensesPath ? 'text-blue-500' : 'text-gray-400'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {expensesOpen && (
                <div className="mt-1 space-y-1">
                  <NavLink
                    to="/expenses"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/expenses'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    Expenses
                  </NavLink>
                  <NavLink
                    to="/expenses/questions"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/expenses/questions'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    All Questions
                  </NavLink>
                  <NavLink
                    to="/expenses/requests"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/expenses/requests'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    Expense Requests
                  </NavLink>
                </div>
              )}
            </div>

            {/* Trips (expandable) */}
            <div>
              <button
                type="button"
                className={`group w-full flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isTripsPath
                    ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                onClick={() => setTripsOpen((v) => !v)}
              >
                <div
                  className={`mr-3 flex-shrink-0 ${
                    isTripsPath ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 6h6m-7 4h8m-9 4h10m-11 6h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v15a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <span className="flex-1 text-left">Trips</span>
                <svg
                  className={`w-5 h-5 transition-transform ${tripsOpen ? 'transform rotate-90' : ''} ${
                    isTripsPath ? 'text-blue-500' : 'text-gray-400'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {tripsOpen && (
                <div className="mt-1 space-y-1">
                  <NavLink
                    to="/trips"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/trips'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    All Trips
                  </NavLink>
                  <NavLink
                    to="/trips/requests"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/trips/requests'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    Trip Requests
                  </NavLink>
                </div>
              )}
            </div>

            {/* Community (expandable) */}
            <div>
              <button
                type="button"
                className={`group w-full flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isCommunityPath
                    ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                onClick={() => setCommunityOpen((v) => !v)}
              >
                <div
                  className={`mr-3 flex-shrink-0 ${
                    isCommunityPath ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z M9 13a1 1 0 11-2 0 1 1 0 012 0z"
                    />
                  </svg>
                </div>
                <span className="flex-1 text-left">Community</span>
                <svg
                  className={`w-5 h-5 transition-transform ${communityOpen ? 'transform rotate-90' : ''} ${
                    isCommunityPath ? 'text-blue-500' : 'text-gray-400'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {communityOpen && (
                <div className="mt-1 space-y-1">
                  <NavLink
                    to="/community"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/community'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    Posts
                  </NavLink>
                  <NavLink
                    to="/community/pending"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/community/pending'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    Pending Posts
                  </NavLink>
                  <NavLink
                    to="/community/showcase"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/community/showcase'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    Showcase
                  </NavLink>
                  <NavLink
                    to="/comments"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/comments'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    Comments
                  </NavLink>
                </div>
              )}
            </div>

            {/* Analytics (expandable) */}
            <div>
              <button
                type="button"
                className={`group w-full flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isAnalyticsPath
                    ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                onClick={() => setAnalyticsOpen((v) => !v)}
              >
                <div
                  className={`mr-3 flex-shrink-0 ${
                    isAnalyticsPath ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <span className="flex-1 text-left">Analytics</span>
                <svg
                  className={`w-5 h-5 transition-transform ${analyticsOpen ? 'transform rotate-90' : ''} ${
                    isAnalyticsPath ? 'text-blue-500' : 'text-gray-400'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {analyticsOpen && (
                <div className="mt-1 space-y-1">
                  <NavLink
                    to="/analytics/cards"
                    className={`group flex items-center pl-11 pr-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      location.pathname === '/analytics/cards'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    Cards
                  </NavLink>
                </div>
              )}
            </div>

            {/* Reward Logs */}
            <NavLink
              to="/reward-logs"
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                location.pathname === '/reward-logs'
                  ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              onClick={onClose}
            >
              <div className={`mr-3 flex-shrink-0 ${location.pathname === '/reward-logs' ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}`}>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              Reward Logs
            </NavLink>
          </nav>

          {/* Bottom section */}
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div>
                <img
                  className="inline-block h-9 w-9 rounded-full"
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt=""
                />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">Admin User</p>
                <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">View profile</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-50 lg:hidden ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
        <div className={`fixed inset-0 bg-gray-600 transition-opacity duration-300 ease-linear ${isOpen ? 'opacity-75' : 'opacity-0'}`} />
        
        <div className={`relative flex-1 flex flex-col max-w-xs w-full bg-white transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          {/* Close button */}
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={onClose}
            >
              <span className="sr-only">Close sidebar</span>
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center px-4">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z"/>
                  </svg>
                </div>
                <h1 className="ml-3 text-xl font-bold text-gray-900">Rewards Admin</h1>
              </div>
            </div>

            {/* Navigation */}
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => {
                const current = isCurrentPath(item.href);
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                      current
                        ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={onClose}
                  >
                    <div className={`mr-4 flex-shrink-0 ${current ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}`}>
                      {item.icon}
                    </div>
                    {item.name}
                    {item.badge && (
                      <span className="ml-auto inline-block py-0.5 px-2 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}

              {/* Expenses (expandable) */}
              <div>
                <button
                  type="button"
                  className={`group w-full flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                    isExpensesPath
                      ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setExpensesOpen((v) => !v)}
                >
                  <div
                    className={`mr-4 flex-shrink-0 ${
                      isExpensesPath ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m0 0l2 2m-2-2l-2-2" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 11h6m-6 4h6" />
                    </svg>
                  </div>
                  <span className="flex-1 text-left">Expenses</span>
                  <svg
                    className={`w-5 h-5 transition-transform ${expensesOpen ? 'transform rotate-90' : ''} ${
                      isExpensesPath ? 'text-blue-500' : 'text-gray-400'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {expensesOpen && (
                  <div className="mt-1 space-y-1">
                    <NavLink
                      to="/expenses"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/expenses'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      Expenses
                    </NavLink>
                    <NavLink
                      to="/expenses/questions"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/expenses/questions'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      All Questions
                    </NavLink>
                    <NavLink
                      to="/expenses/requests"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/expenses/requests'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      Expense Requests
                    </NavLink>
                  </div>
                )}
              </div>

              {/* Trips (expandable) */}
              <div>
                <button
                  type="button"
                  className={`group w-full flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                    isTripsPath
                      ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setTripsOpen((v) => !v)}
                >
                  <div
                    className={`mr-4 flex-shrink-0 ${
                      isTripsPath ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 6h6m-7 4h8m-9 4h10m-11 6h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v15a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <span className="flex-1 text-left">Trips</span>
                  <svg
                    className={`w-5 h-5 transition-transform ${tripsOpen ? 'transform rotate-90' : ''} ${
                      isTripsPath ? 'text-blue-500' : 'text-gray-400'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {tripsOpen && (
                  <div className="mt-1 space-y-1">
                    <NavLink
                      to="/trips"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/trips'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      All Trips
                    </NavLink>
                    <NavLink
                      to="/trips/requests"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/trips/requests'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      Trip Requests
                    </NavLink>
                  </div>
                )}
              </div>

              {/* Community (expandable) */}
              <div>
                <button
                  type="button"
                  className={`group w-full flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                    isCommunityPath
                      ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setCommunityOpen((v) => !v)}
                >
                  <div
                    className={`mr-4 flex-shrink-0 ${
                      isCommunityPath ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z M9 13a1 1 0 11-2 0 1 1 0 012 0z"
                      />
                    </svg>
                  </div>
                  <span className="flex-1 text-left">Community</span>
                  <svg
                    className={`w-5 h-5 transition-transform ${communityOpen ? 'transform rotate-90' : ''} ${
                      isCommunityPath ? 'text-blue-500' : 'text-gray-400'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {communityOpen && (
                  <div className="mt-1 space-y-1">
                    <NavLink
                      to="/community"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/community'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      Posts
                    </NavLink>
                    <NavLink
                      to="/community/pending"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/community/pending'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      Pending Posts
                    </NavLink>
                    <NavLink
                      to="/community/showcase"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/community/showcase'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      Showcase
                    </NavLink>
                    <NavLink
                      to="/comments"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/comments'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      Comments
                    </NavLink>
                  </div>
                )}
              </div>

              {/* Analytics (expandable) */}
              <div>
                <button
                  type="button"
                  className={`group w-full flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                    isAnalyticsPath
                      ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setAnalyticsOpen((v) => !v)}
                >
                  <div
                    className={`mr-4 flex-shrink-0 ${
                      isAnalyticsPath ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <span className="flex-1 text-left">Analytics</span>
                  <svg
                    className={`w-5 h-5 transition-transform ${analyticsOpen ? 'transform rotate-90' : ''} ${
                      isAnalyticsPath ? 'text-blue-500' : 'text-gray-400'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {analyticsOpen && (
                  <div className="mt-1 space-y-1">
                    <NavLink
                      to="/analytics/cards"
                      className={`group flex items-center pl-12 pr-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                        location.pathname === '/analytics/cards'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={onClose}
                    >
                      Cards
                    </NavLink>
                  </div>
                )}
              </div>

              {/* Reward Logs */}
              <NavLink
                to="/reward-logs"
                className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-150 ${
                  location.pathname === '/reward-logs'
                    ? 'bg-blue-50 border-r-4 border-blue-500 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                onClick={onClose}
              >
                <div className={`mr-4 flex-shrink-0 ${location.pathname === '/reward-logs' ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}`}>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                Reward Logs
              </NavLink>
            </nav>
          </div>

          {/* Bottom section */}
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div>
                <img
                  className="inline-block h-10 w-10 rounded-full"
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt=""
                />
              </div>
              <div className="ml-3">
                <p className="text-base font-medium text-gray-700">Admin User</p>
                <p className="text-sm font-medium text-gray-500">View profile</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 w-14">
          {/* Force sidebar to shrink to fit close icon */}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
